import axios from 'axios'

export default {
  namespaced: true,
  state: {
    articles: [],
    article: null,
    featuredArticles: [],
    recommendedArticles: [],
    trendingArticles: [],
    latestArticles: [],
    pagination: {
      page: 1,
      limit: 10,
      totalPages: 1,
      total: 0
    },
    activeTab: 'latest'
  },
  getters: {
    articles: state => state.articles,
    article: state => state.article,
    featuredArticles: state => state.featuredArticles,
    recommendedArticles: state => state.recommendedArticles,
    trendingArticles: state => state.trendingArticles,
    latestArticles: state => state.latestArticles,
    pagination: state => state.pagination,
    activeTab: state => state.activeTab
  },
  mutations: {
    SET_ARTICLES(state, articles) {
      state.articles = articles
    },
    ADD_ARTICLES(state, articles) {
      state.articles = [...state.articles, ...articles]
    },
    SET_ARTICLE(state, article) {
      state.article = article
    },
    SET_FEATURED_ARTICLES(state, articles) {
      state.featuredArticles = articles
    },
    SET_RECOMMENDED_ARTICLES(state, articles) {
      state.recommendedArticles = articles
    },
    SET_TRENDING_ARTICLES(state, articles) {
      state.trendingArticles = articles
    },
    SET_LATEST_ARTICLES(state, articles) {
      state.latestArticles = articles
    },
    SET_PAGINATION(state, pagination) {
      state.pagination = pagination
    },
    INCREMENT_PAGE(state) {
      state.pagination.page += 1
    },
    SET_ACTIVE_TAB(state, tab) {
      state.activeTab = tab
    }
  },
  actions: {
    async fetchArticles({ commit, dispatch, state }, { page = 1, limit = 10, category, source, tag, sort } = {}) {
      try {
        dispatch('setLoading', true, { root: true })
        
        const params = { page, limit }
        if (category) params.category = category
        if (source) params.source = source
        if (tag) params.tag = tag
        if (sort) params.sort = sort
        
        const response = await axios.get('/api/articles', { params })
        const { articles, pagination } = response.data.data
        
        if (page === 1) {
          commit('SET_ARTICLES', articles)
        } else {
          commit('ADD_ARTICLES', articles)
        }
        
        commit('SET_PAGINATION', pagination)
        return response.data
      } catch (error) {
        dispatch('setError', error.response?.data?.message || 'Erreur de récupération des articles', { root: true })
        throw error
      } finally {
        dispatch('setLoading', false, { root: true })
      }
    },
    
    async fetchArticle({ commit, dispatch }, id) {
      try {
        dispatch('setLoading', true, { root: true })
        const response = await axios.get(`/api/articles/${id}`)
        const { article, similarArticles } = response.data.data
        
        commit('SET_ARTICLE', article)
        return { ...article, similarArticles }
      } catch (error) {
        dispatch('setError', error.response?.data?.message || 'Erreur de récupération de l\'article', { root: true })
        throw error
      } finally {
        dispatch('setLoading', false, { root: true })
      }
    },
    
    async fetchArticleBySlug({ commit, dispatch }, slug) {
      try {
        dispatch('setLoading', true, { root: true })
        const response = await axios.get(`/api/articles/slug/${slug}`)
        const { article, similarArticles } = response.data.data
        
        commit('SET_ARTICLE', article)
        return { ...article, similarArticles }
      } catch (error) {
        dispatch('setError', error.response?.data?.message || 'Erreur de récupération de l\'article', { root: true })
        throw error
      } finally {
        dispatch('setLoading', false, { root: true })
      }
    },
    
    async fetchFeaturedArticles({ commit, dispatch }) {
      try {
        dispatch('setLoading', true, { root: true })
        const response = await axios.get('/api/articles/featured')
        const { trending } = response.data.data
        
        commit('SET_FEATURED_ARTICLES', trending.slice(0, 3)) // Prendre les 3 premiers articles tendance
        return trending
      } catch (error) {
        dispatch('setError', error.response?.data?.message || 'Erreur de récupération des articles à la une', { root: true })
        throw error
      } finally {
        dispatch('setLoading', false, { root: true })
      }
    },
    
    async fetchRecommendedArticles({ commit, dispatch }) {
      try {
        dispatch('setLoading', true, { root: true })
        const response = await axios.get('/api/articles/recommended')
        const { articles } = response.data.data
        
        commit('SET_RECOMMENDED_ARTICLES', articles)
        return articles
      } catch (error) {
        dispatch('setError', error.response?.data?.message || 'Erreur de récupération des articles recommandés', { root: true })
        throw error
      } finally {
        dispatch('setLoading', false, { root: true })
      }
    },
    
    async fetchTrendingArticles({ commit, dispatch }) {
      try {
        dispatch('setLoading', true, { root: true })
        const response = await axios.get('/api/articles/featured')
        const { trending } = response.data.data
        
        commit('SET_TRENDING_ARTICLES', trending)
        return trending
      } catch (error) {
        dispatch('setError', error.response?.data?.message || 'Erreur de récupération des articles tendance', { root: true })
        throw error
      } finally {
        dispatch('setLoading', false, { root: true })
      }
    },
    
    async fetchLatestArticles({ commit, dispatch }) {
      try {
        dispatch('setLoading', true, { root: true })
        const response = await axios.get('/api/articles', { params: { sort: 'newest', limit: 20 } })
        const { articles } = response.data.data
        
        commit('SET_LATEST_ARTICLES', articles)
        return articles
      } catch (error) {
        dispatch('setError', error.response?.data?.message || 'Erreur de récupération des derniers articles', { root: true })
        throw error
      } finally {
        dispatch('setLoading', false, { root: true })
      }
    },
    
    async fetchGrid({ commit, dispatch }, options = {}) {
      try {
        dispatch('setLoading', true, { root: true })
        const response = await axios.get('/api/grid', { params: options })
        
        return response.data.data;
      } catch (error) {
        dispatch('setError', error.response?.data?.message || 'Erreur de récupération de la grille', { root: true })
        throw error
      } finally {
        dispatch('setLoading', false, { root: true })
      }
    },
    
    setActiveTab({ commit }, tab) {
      commit('SET_ACTIVE_TAB', tab)
    },
    
    loadMore({ commit, dispatch, state }) {
      if (state.pagination.page < state.pagination.totalPages) {
        commit('INCREMENT_PAGE')
        return dispatch('fetchArticles', { 
          page: state.pagination.page,
          limit: state.pagination.limit 
        })
      }
      return Promise.resolve()
    }
  }
}